import { ref, toValue } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useDevOnly } from '@/composables/useDevOnly';

// A Chrome content script is not injected into tabs that are already open, so the extension can
// set its marker a moment after the app mounts. Reading the marker once would report "not
// installed" for a user who has it. Re-check across a short window before concluding it's absent:
// half a second is well under the delay a user would notice, and three seconds is far longer than
// a content script needs to run at document_idle.
const DETECTION_POLL_INTERVAL_MS = 500;
const DETECTION_WINDOW_MS = 3000;

/**
 * True only on the production deployment. Vite's `import.meta.env.PROD` can't tell us this: every
 * deployed build is a production build, QA included, so it is `true` there too — and the reminder
 * dialog's scrim then blocks Cypress on QA. The server knows where it runs: outside prod it opens
 * every feature flag and puts `'*'` in the token's `newFeatures` (#6224). `isDev()` additionally
 * covers local dev and the `freshqa` tenants Cypress runs against.
 */
export const isProductionEnvironment = () => !useDevOnly().isDev() && !useAuthStore().newFeatures.includes('*');

/**
 * Detects the Perspectives browser extension via the `window.PERSPECTIVES_INSTALLED` marker it
 * sets. That marker is only ever set in production — dev and QA builds have no content script to
 * inject it — so outside production this reports installed (assume present, never nag) without
 * touching `window` or starting a timer. Shared by the app-bar install button and the setup
 * reminder dialog so the two apply the same polling logic and production gate (#5823).
 *
 * `hasExtension` starts `true` (assume installed) and flips to `false` at most once, only once the
 * retry window has genuinely concluded the marker is absent — it never flashes `false` during the
 * retry window itself.
 */
export function usePerspectivesExtensionDetection({ hasAccess, isProduction = isProductionEnvironment() } = {}) {
    const hasExtension = ref(true);
    let timerId = null;

    const stop = () => {
        if (timerId !== null) {
            clearInterval(timerId);
            timerId = null;
        }
    };

    // Without the permission there is nothing to detect, and outside production the marker can
    // never appear — don't touch storage-adjacent globals or start a timer for either case.
    if (!isProduction || !toValue(hasAccess)) {
        return { hasExtension, stop };
    }

    const extensionDetected = () => !!window.PERSPECTIVES_INSTALLED;

    if (extensionDetected()) {
        return { hasExtension, stop };
    }

    let elapsedMs = 0;
    timerId = setInterval(() => {
        elapsedMs += DETECTION_POLL_INTERVAL_MS;
        if (extensionDetected()) {
            stop();
            return;
        }
        if (elapsedMs >= DETECTION_WINDOW_MS) {
            hasExtension.value = false;
            stop();
        }
    }, DETECTION_POLL_INTERVAL_MS);

    return { hasExtension, stop };
}
